import type { Query, QueryClient } from '@tanstack/react-query';

/**
 * EX-10 — what a language switch asks again (CONTRACT §4).
 *
 * The server words status labels, next actions, holders and display names in
 * the language of the request that fetched them (`X-Fidelen-Locale`), so after
 * a switch the cached answers are in the old language. They are INVALIDATED,
 * not reset or removed: each keeps its data while the new answer is fetched,
 * so no page goes back to "loading", nothing remounts, and a form being typed
 * into, an open tab, a filter or a chosen file stays exactly as it was. Only
 * the queries on screen are fetched again (`refetchType: 'active'`), and a
 * request still in flight in the old language is cancelled first; the rest
 * are fetched in the new language when they are next used.
 *
 * Left alone:
 * - `['activation', token]` and `['secure-action', token]` — a token link is
 *   read once; asking again could spend or audit it;
 * - any query that says `meta: { localeRefetch: false }` — one that already
 *   holds every language (Quick Find's bilingual titles), or a form prefill
 *   that must not run twice;
 * - a read that FAILED and holds nothing — a signed-out session (401), a list
 *   refused by permission. It has no words in the old language to replace
 *   (a refusal is said from its error code, in whatever language is current),
 *   and asking again is not harmless: TanStack Query puts a query with no data
 *   back to `pending` for the refetch, so a gate that draws "loading" for it
 *   unmounted what it showed. Signed out, that was the sign-in screen: the
 *   address being typed, and step 2's organisation choice with its
 *   single-use token, were gone the moment the person switched language (W11).
 */
const TOKEN_READS = new Set(['activation', 'secure-action']);

function failedWithNothing(query: Query): boolean {
  return query.state.status === 'error' && query.state.data === undefined;
}

export function refetchesOnLocaleChange(query: Query): boolean {
  const [head] = query.queryKey;

  if (typeof head === 'string' && TOKEN_READS.has(head)) return false;
  if (failedWithNothing(query)) return false;

  return query.meta?.localeRefetch !== false;
}

export async function refetchForLocale(queryClient: QueryClient): Promise<void> {
  /*
   * A request still in flight was asked in the old language. Cancelled first —
   * TanStack would otherwise let a FIRST load (no data yet) finish and show
   * its old-language answer — and reverted, so a page keeps what it had.
   */
  await queryClient.cancelQueries({ predicate: refetchesOnLocaleChange, fetchStatus: 'fetching' });
  await queryClient.invalidateQueries(
    { predicate: refetchesOnLocaleChange, refetchType: 'active' },
    { cancelRefetch: true },
  );
}
